import { Fragment, useCallback, useEffect, useState, useMemo } from "react";
import axios from "axios";
import { slugSplitter } from "../../../utils/slugManager";
import config from "../../../configuration/config";
import { useTranslation } from "react-i18next";
import { groupBy, mapValues, omit } from "lodash";
import { Price } from "../../../components/__simple/_price";
import { generateVehicleSpecsPDF } from "../../../utils/generateVehicleSpecsPDF";
import Skeleton from "react-loading-skeleton";
import { EmptyState } from "../../../components/EmptyState";
import { Swiper, SwiperSlide } from "swiper/react";


export function VehicleComparisonPage({ vehicle }) {
    const { t: trans, i18n } = useTranslation(["common", "vehicales", "info", "car"]);
    const [models, setModels] = useState([]);
    const [selectedModelIds, setSelectedModelIds] = useState(new Set());
    const [backUpImage, setBackUpImage] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [allFeatures, setAllFeatures] = useState({});
    const [showDifferencesOnly, setShowDifferencesOnly] = useState(false);
    const [isPdfReady, setIsPdfReady] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
    const [activeSpecCat, setActiveSpecCat] = useState(null);

    let lang = (i18n.language || 'ar');

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchModels = useCallback(async (vehicle) => {
        setIsLoading(true);
        try {
            const response = await axios.request({
                baseURL: config.controllURL,
                url: lang + "/api/VehicleSpecs/" + slugSplitter(vehicle?.group) + "/" + vehicle?.year + "/specs",
                method: "get",
            });

            const filteredModels = response.data.models.filter(m => {
                const combinedFields = `${m.modelDescription} ${m.name} ${m.type}`.toLowerCase();
                return !combinedFields.includes("1l5");
            });

            setModels(filteredModels);
            setBackUpImage(response.data.backupImage);

            // Initialize with first two models by default if available
            if (filteredModels.length > 0) {
                const initial = new Set();
                initial.add(filteredModels[0].productTarget);
                if (filteredModels.length > 1) initial.add(filteredModels[1].productTarget);
                setSelectedModelIds(initial);
            }

        } catch (error) {
            console.error("Failed to fetch models", error);
        } finally {
            setIsLoading(false);
        }
    }, [lang]);

    useEffect(() => {
        if (vehicle?.group) fetchModels(vehicle);
    }, [vehicle, fetchModels]);

    // Fetch features for all models (needed for comparison)
    useEffect(() => {
        if (models.length > 0) {
            models.forEach(async (model) => {
                if (!allFeatures[model.productTarget]) {
                    try {
                        const response = await axios.post(`${config.controllURL}${lang}/api/Pages/Fetch_InvProductsEquipments`, {
                            "GUID": `${model.productTarget}`
                        });
                        const groupResult = mapValues(groupBy(response.data, 'ID'), fList => fList.map(items => omit(items, 'ID')));
                        let dataFinal = [];
                        Object.keys(groupResult).forEach((key) => {
                            let item = null;
                            let combinedText = "";
                            groupResult[key].forEach((a, i) => {
                                const textAr = [a.NoteAr, a.DescriptionAr].filter(t => t && t.trim()).join('\n').trim();
                                const textEn = [a.NoteEn, a.DescriptionEn].filter(t => t && t.trim()).join('\n').trim();

                                if (lang === "ar") {
                                    if (textAr) combinedText = combinedText + textAr + "\n";
                                } else {
                                    if (textEn) combinedText = combinedText + textEn + "\n";
                                }

                                if (i === (groupResult[key].length - 1)) {
                                    item = { ...a, note: combinedText.trim() };
                                }
                            });
                            if (item) dataFinal.push(item);
                        });
                        setAllFeatures(prev => ({ ...prev, [model.productTarget]: dataFinal }));
                    } catch (err) {
                        console.error("Failed to fetch features for " + model.productTarget, err);
                    }
                }
            });
        }
    }, [models, lang, allFeatures]);

    const unifiedCats = useMemo(() => {
        const cats = [];
        Object.values(allFeatures).forEach(modelFeatures => {
            modelFeatures.forEach(f => {
                const sectionName = (lang === 'ar' ? f.sectionAr : f.sectionEn)?.trim();
                if (!sectionName) return;

                let cat = cats.find(c => c.title === sectionName);
                if (!cat) {
                    cat = { title: sectionName, rows: [] };
                    cats.push(cat);
                }

                const note = f.note || "";
                note.split('\n').forEach(line => {
                    const label = line.split(':')[0]?.trim();
                    if (label && label !== "null" && !cat.rows.includes(label)) {
                        cat.rows.push(label);
                    }
                });
            });
        });
        return cats;
    }, [allFeatures, lang]);

    const specsMatrix = useMemo(() => {
        const matrix = {};
        Object.entries(allFeatures).forEach(([modelId, features]) => {
            matrix[modelId] = {};
            features.forEach(f => {
                const sectionName = (lang === 'ar' ? f.sectionAr : f.sectionEn)?.trim();
                if (!sectionName) return;
                if (!matrix[modelId][sectionName]) matrix[modelId][sectionName] = {};

                const note = f.note || "";
                note.split('\n').forEach(line => {
                    const parts = line.split(':');
                    const label = parts[0]?.trim();
                    if (label && label !== "null") {
                        matrix[modelId][sectionName][label] = parts.slice(1).join(':').trim() || '✓';
                    }
                });
            });
        });
        return matrix;
    }, [allFeatures, lang]);

    const getSpecValue = useCallback((modelTarget, sectionTitle, rowLabel) => {
        return specsMatrix[modelTarget]?.[sectionTitle]?.[rowLabel] || null;
    }, [specsMatrix]);

    const toggleModelSelection = (id) => {
        setSelectedModelIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                if (next.size > 1) next.delete(id); // Keep at least one
            } else {
                next.add(id); // Unlimited models
            }
            return next;
        });
        setIsPdfReady(false);
    };

    const selectedModelsList = models.filter(m => selectedModelIds.has(m.productTarget));

    const checkIsDifferent = (catTitle, rowLabel) => {
        if (selectedModelIds.size <= 1) return false;
        let values = new Set();

        selectedModelsList.forEach(m => {
            const rawVal = getSpecValue(m.productTarget, catTitle, rowLabel);
            const normalizedVal = (rawVal === null || rawVal === undefined || String(rawVal).trim() === '' || String(rawVal).trim() === '—' || String(rawVal).trim() === 'null') ? '—' : String(rawVal).trim();
            values.add(normalizedVal);
        });

        return values.size > 1;
    };

    const renderValue = (val) => {
        if (!val || val === '—' || val === 'null') return <span className="ck ck-n">—</span>;
        if (val === '✓' || val.toLowerCase() === 'check' || val.toLowerCase() === 'yes') return <span className="ck ck-y"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"></polyline></svg></span>;
        return <div className="v">{val}</div>;
    };

    const handleDownloadPdf = () => {
        setIsPdfReady(true);
        generateVehicleSpecsPDF({
            lang,
            vehicle,
            models: selectedModelsList,
            unifiedCats,
            getSpecValue,
            trans
        });
        setTimeout(() => setIsPdfReady(false), 2000);
    };

    if (isLoading) {
        return (
            <div className="container py-20">
                <Skeleton height={50} count={1} borderRadius={12} className="mb-4" />
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <Skeleton height={120} borderRadius={18} count={4} />
                </div>
                <Skeleton height={400} borderRadius={24} />
            </div>
        );
    }

    if (models.length === 0) {
        return <div className="container py-20"><EmptyState message={trans("vehicales:no-specs-data")} /></div>;
    }

    return (
        <div className="comparison-container container">
            <div className="comparison-header-section">
                <div className="stag">{trans("vehicales:comparison_tool")}</div>
                <h2 className="sh">{trans("vehicales:compare")} <span className="gn">{trans("vehicales:models")}</span></h2>
                <p className="sub-header">{trans("vehicales:compare_desc")}</p>
            </div>

            <div className="comparison-header-actions">
                <div className="action-left" style={{ display: 'flex', gap: '12px', flex: isMobile ? '1' : 'none' }}>
                    <button
                        className={`diff-toggle-btn ${showDifferencesOnly ? 'active' : ''}`}
                        onClick={() => setShowDifferencesOnly(!showDifferencesOnly)}
                        style={{ flex: isMobile ? '1' : 'none' }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M16 3h5v5M8 21H3v-5M21 3l-7 7M3 21l7-7M15 15l6 6M9 9l-6-6" /></svg>
                        {trans("vehicales:differences_only")}
                    </button>

                    {isMobile && (
                        <div className="action-right" style={{ flex: '1' }}>
                            <button className="pdf-btn-top" style={{ width: '100%' }} onClick={handleDownloadPdf} disabled={isPdfReady}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
                                {isPdfReady ? trans("vehicales:generating") : trans("vehicales:export_pdf")}
                            </button>
                        </div>
                    )}
                </div>

                {!isMobile && (
                    <div className="action-right">
                        <button className="pdf-btn-top" onClick={handleDownloadPdf} disabled={isPdfReady}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
                            {isPdfReady ? trans("vehicales:generating") : trans("vehicales:prepare_pdf")}
                        </button>
                    </div>
                )}
            </div>

            <div className="model-selector-tabs" style={{ maxWidth: '100%' }}>
                <Swiper
                    slidesPerView={"auto"}
                    spaceBetween={12}
                    centerInsufficientSlides={true}
                    slidesOffsetBefore={20}
                    slidesOffsetAfter={20}
                    className="tabs-swiper"
                    style={{ padding: '8px 0' }}
                >
                    {models.map(m => (
                        <SwiperSlide key={m.productTarget} style={{ width: 'auto' }}>
                            <div
                                className={`model-chip ${selectedModelIds.has(m.productTarget) ? 'selected' : ''}`}
                                onClick={() => toggleModelSelection(m.productTarget)}
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                <div className="chip-check">
                                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="4"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                </div>
                                <span className="chip-label">{m.modelDescription}</span>
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>

            {/* DESKTOP TABLE */}
            {!isMobile && (
                <div className="comparison-table-wrap">
                    <table className="comp-table">
                        <thead>
                            <tr>
                                <th className="sticky-cell">{trans("vehicales:spec_label")}</th>
                                {selectedModelsList.map(m => (
                                    <th key={m.productTarget}>
                                        <div className="model-header-cell">
                                            <div className="m-img-wrap">
                                                <img src={m.imagePath ? config.mainURL + m.imagePath : config.mainURL + backUpImage} alt={m.modelDescription} />
                                            </div>
                                            <div className="m-info">
                                                <div className="m-name">{m.modelDescription}</div>
                                                <div className="m-price"><Price price={m.minPrice} /></div>
                                            </div>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {unifiedCats.map((cat, cIdx) => {
                                const rowsToShow = showDifferencesOnly
                                    ? cat.rows.filter(r => checkIsDifferent(cat.title, r))
                                    : cat.rows;

                                if (rowsToShow.length === 0) return null;

                                return (
                                    <Fragment key={cIdx}>
                                        <tr className="section-header-row">
                                            <td colSpan={selectedModelIds.size + 1}>
                                                <div className="sec-title-wrap">
                                                    <div className="sec-icon">
                                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" /></svg>
                                                    </div>
                                                    <span className="sec-title-text">{cat.title}</span>
                                                </div>
                                            </td>
                                        </tr>
                                        {rowsToShow.map((rowLabel, rIdx) => {
                                            const isDiff = checkIsDifferent(cat.title, rowLabel);
                                            return (
                                                <tr key={rIdx} className={isDiff ? 'diff-row' : ''}>
                                                    <td className="sticky-cell">{rowLabel}</td>
                                                    {selectedModelsList.map(m => (
                                                        <td key={m.productTarget} className="val-cell">
                                                            {renderValue(getSpecValue(m.productTarget, cat.title, rowLabel))}
                                                        </td>
                                                    ))}
                                                </tr>
                                            );
                                        })}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* MOBILE SECTION LIST */}
            {isMobile && (
                <div className="mobile-spec-sections">
                    {unifiedCats.map((cat, idx) => {
                        const rowsToShow = showDifferencesOnly
                            ? cat.rows.filter(r => checkIsDifferent(cat.title, r))
                            : cat.rows;

                        if (rowsToShow.length === 0) return null;

                        return (
                            <div key={idx} className="spec-card-v7" onClick={() => setActiveSpecCat(cat)}>
                                <div className="card-inner">
                                    <div className="card-icon">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" /></svg>
                                    </div>
                                    <div className="card-content">
                                        <span className="card-title">{cat.title}</span>
                                    </div>
                                    <div className="card-arrow">
                                        {lang === 'ar' ? '←' : '→'}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* MOBILE MODAL */}
            {activeSpecCat && (
                <div className="spec-modal-overlay" onClick={() => setActiveSpecCat(null)}>
                    <div className="spec-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="spec-modal-header">
                            <div className="header-top">
                                <h2 className="spec-modal-title">{activeSpecCat.title}</h2>
                                <button className="spec-modal-close" onClick={() => setActiveSpecCat(null)}>
                                    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                                </button>
                            </div>
                        </div>
                        <div className="spec-modal-body">
                            <div className="spec-mobile-cards">
                                {activeSpecCat.rows.map((rowLabel, rIdx) => {
                                    const isDiff = checkIsDifferent(activeSpecCat.title, rowLabel);
                                    if (showDifferencesOnly && !isDiff) return null;

                                    return (
                                        <div key={rIdx} className={`spec-mob-card ${isDiff ? 'diff-highlight' : ''}`}>
                                            <div className="spec-mob-label">{rowLabel}</div>
                                            <div className="spec-mob-values">
                                                {selectedModelsList.map((m, vIdx) => (
                                                    <div key={vIdx} className="spec-mob-value-row">
                                                        <div className="model-name">
                                                            <div className={`type-dot ${m.hybird ? 'hybrid' : 'petrol'}`}></div>
                                                            {m.modelDescription}
                                                        </div>
                                                        <div className="model-val">{renderValue(getSpecValue(m.productTarget, activeSpecCat.title, rowLabel))}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
